import { create } from "zustand";
import { supabase } from "../lib/supabase";

export function extractProfile(profile: unknown): Comment["profile"] {
  if (Array.isArray(profile)) return profile[0] ?? null;
  return (profile as Comment["profile"]) ?? null;
}

export interface Comment {
  id: string;
  activity_id: string;
  user_id: string;
  content: string;
  created_at: string;
  profile: {
    id: string;
    username: string | null;
    full_name: string | null;
    avatar_url: string | null;
  } | null;
}

interface CommentState {
  commentsByActivity: Record<string, Comment[]>;
  commentCountByActivity: Record<string, number>;
  loadingByActivity: Record<string, boolean>;
  errorByActivity: Record<string, string | null>;

  setPreviewComments: (activityId: string, comments: Comment[], count: number) => void;
  fetchComments: (activityId: string) => Promise<void>;
  addComment: (activityId: string, content: string) => Promise<void>;
  deleteComment: (commentId: string, activityId: string) => Promise<void>;
}

export const useCommentStore = create<CommentState>((set, get) => ({
  commentsByActivity: {},
  commentCountByActivity: {},
  loadingByActivity: {},
  errorByActivity: {},

  setPreviewComments: (activityId, comments, count) => {
    set((state) => ({
      commentsByActivity: {
        ...state.commentsByActivity,
        [activityId]: comments,
      },
      commentCountByActivity: {
        ...state.commentCountByActivity,
        [activityId]: count,
      },
    }));
  },

  fetchComments: async (activityId) => {
    set((state) => ({
      loadingByActivity: { ...state.loadingByActivity, [activityId]: true },
      errorByActivity: { ...state.errorByActivity, [activityId]: null },
    }));

    try {
      const { data, error } = await supabase
        .from("comments")
        .select(
          `
          id,
          activity_id,
          user_id,
          content,
          created_at,
          profile:profiles!user_id (
            id,
            username,
            full_name,
            avatar_url
          )
        `
        )
        .eq("activity_id", activityId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      const comments: Comment[] = (data || []).map((item: Record<string, unknown>) => ({
        id: item.id as string,
        activity_id: item.activity_id as string,
        user_id: item.user_id as string,
        content: item.content as string,
        created_at: item.created_at as string,
        profile: extractProfile(item.profile),
      }));

      set((state) => ({
        commentsByActivity: {
          ...state.commentsByActivity,
          [activityId]: comments,
        },
        commentCountByActivity: {
          ...state.commentCountByActivity,
          [activityId]: comments.length,
        },
        loadingByActivity: { ...state.loadingByActivity, [activityId]: false },
      }));
    } catch (error) {
      console.error("Failed to fetch comments:", error);
      set((state) => ({
        loadingByActivity: { ...state.loadingByActivity, [activityId]: false },
        errorByActivity: {
          ...state.errorByActivity,
          [activityId]: "Failed to load comments",
        },
      }));
    }
  },

  addComment: async (activityId, content) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      throw new Error("Must be logged in to comment");
    }

    const trimmedContent = content.trim();
    const tempId = `temp-${Date.now()}`;
    const tempComment: Comment = {
      id: tempId,
      activity_id: activityId,
      user_id: user.id,
      content: trimmedContent,
      created_at: new Date().toISOString(),
      profile: null,
    };

    // Optimistic update
    set((state) => ({
      commentsByActivity: {
        ...state.commentsByActivity,
        [activityId]: [...(state.commentsByActivity[activityId] || []), tempComment],
      },
      commentCountByActivity: {
        ...state.commentCountByActivity,
        [activityId]: (state.commentCountByActivity[activityId] || 0) + 1,
      },
    }));

    try {
      const { data, error } = await supabase
        .from("comments")
        .insert({
          activity_id: activityId,
          user_id: user.id,
          content: trimmedContent,
        })
        .select(
          `
          id,
          activity_id,
          user_id,
          content,
          created_at,
          profile:profiles!user_id (
            id,
            username,
            full_name,
            avatar_url
          )
        `
        )
        .single();

      if (error) throw error;

      const realComment: Comment = {
        id: data.id as string,
        activity_id: data.activity_id as string,
        user_id: data.user_id as string,
        content: data.content as string,
        created_at: data.created_at as string,
        profile: extractProfile(data.profile),
      };

      // Replace temp comment with real one
      set((state) => ({
        commentsByActivity: {
          ...state.commentsByActivity,
          [activityId]: (state.commentsByActivity[activityId] || []).map((c) =>
            c.id === tempId ? realComment : c
          ),
        },
      }));
    } catch (error) {
      console.error("Failed to add comment:", error);
      // Rollback optimistic update
      set((state) => ({
        commentsByActivity: {
          ...state.commentsByActivity,
          [activityId]: (state.commentsByActivity[activityId] || []).filter(
            (c) => c.id !== tempId
          ),
        },
        commentCountByActivity: {
          ...state.commentCountByActivity,
          [activityId]: Math.max(
            0,
            (state.commentCountByActivity[activityId] || 1) - 1
          ),
        },
        errorByActivity: {
          ...state.errorByActivity,
          [activityId]: "Failed to add comment",
        },
      }));
      throw error;
    }
  },

  deleteComment: async (commentId, activityId) => {
    const comments = get().commentsByActivity[activityId] || [];
    const deletedComment = comments.find((c) => c.id === commentId);

    // Optimistic update
    set((state) => ({
      commentsByActivity: {
        ...state.commentsByActivity,
        [activityId]: (state.commentsByActivity[activityId] || []).filter(
          (c) => c.id !== commentId
        ),
      },
      commentCountByActivity: {
        ...state.commentCountByActivity,
        [activityId]: Math.max(
          0,
          (state.commentCountByActivity[activityId] || 1) - 1
        ),
      },
    }));

    try {
      const { error } = await supabase
        .from("comments")
        .delete()
        .eq("id", commentId);

      if (error) throw error;
    } catch (error) {
      console.error("Failed to delete comment:", error);
      // Rollback
      if (deletedComment) {
        set((state) => {
          const current = state.commentsByActivity[activityId] || [];
          const restored = [...current, deletedComment].sort(
            (a, b) =>
              new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
          return {
            commentsByActivity: {
              ...state.commentsByActivity,
              [activityId]: restored,
            },
            commentCountByActivity: {
              ...state.commentCountByActivity,
              [activityId]: (state.commentCountByActivity[activityId] || 0) + 1,
            },
            errorByActivity: {
              ...state.errorByActivity,
              [activityId]: "Failed to delete comment",
            },
          };
        });
      }
      throw error;
    }
  },
}));
